import React, { useEffect, useMemo, useState } from "react";
import { useEmailSections } from "../../Context/EmailSectionContext";
import StyleControls from "./StyleControls";

/**
 * Description_btn
 * Renders ONLY a centered CTA button (like the screenshot).
 *
 * Supports API shape:
 * {
 *   component_name: "Description_btn",
 *   features: [{ title: "...", link: "#", cta: "..." }]
 * }
 */
const DescriptionBtn = (props) => {
  const { id, previewMode, styleSettings = {}, data } = props;
  const { sections, updateSection } = useEmailSections();

  const sectionData = previewMode
    ? data || props
    : sections.find((section) => section.id === id) || {};

  const {
    features = [],
    link: fallbackLink = "#",
    button_color,
    buttonColor,
    text_color,
    textColor,
    component_name = "Description_btn",
  } = sectionData;

  const firstFeature = Array.isArray(features) && features.length > 0 ? features[0] : null;

  const initialLabel =
    firstFeature?.title || firstFeature?.cta || firstFeature?.button_text || "Click Here";
  const initialHref = firstFeature?.link || firstFeature?.href || firstFeature?.url || fallbackLink || "#";

  const initialBtnColor =
    button_color ||
    buttonColor ||
    styleSettings?.primaryColor ||
    "#e11d48"; // red default like screenshot
  const initialTextColor =
    text_color ||
    textColor ||
    styleSettings?.buttonTextColor ||
    "#ffffff";

  const [isEditing, setIsEditing] = useState(false);
  const [label, setLabel] = useState(initialLabel);
  const [href, setHref] = useState(initialHref);
  const [bg, setBg] = useState(initialBtnColor);
  const [fg, setFg] = useState(initialTextColor);

  // Keep local state in sync with incoming data (streaming)
  useEffect(() => {
    const f = Array.isArray(sectionData.features) && sectionData.features.length > 0 ? sectionData.features[0] : null;
    setLabel(f?.title || f?.cta || f?.button_text || "Click Here");
    setHref(f?.link || f?.href || f?.url || sectionData.link || "#");
    setBg(sectionData.button_color || sectionData.buttonColor || styleSettings?.primaryColor || "#e11d48");
    setFg(sectionData.text_color || sectionData.textColor || styleSettings?.buttonTextColor || "#ffffff");
  }, [id, sectionData, styleSettings]);

  // Merge section-specific styles with parent styles
  const sectionStyleSettings = sectionData?.styleSettings || {};
  const effectiveStyleSettings = {
    ...styleSettings,  // Parent styles (defaults)
    ...sectionStyleSettings  // Section-specific overrides
  };
  const {
    fontFamily = "helvetica, 'helvetica neue', arial, verdana, sans-serif",
    backgroundColor = "#ffffff"
  } = effectiveStyleSettings;

  const buttonStyle = useMemo(
    () => ({
      backgroundColor: bg,
      color: fg,
      padding: "12px 22px",
      borderRadius: 2,
      textDecoration: "none",
      fontWeight: 600,
      display: "inline-block",
      minWidth: 220,
      textAlign: "center",
      fontFamily,
      border: "none",
    }),
    [bg, fg, fontFamily]
  );

  const containerStyle = useMemo(
    () => ({
      padding: "28px 16px",
      display: "flex",
      justifyContent: "center",
    }),
    []
  );

  const handleSave = () => {
    const currentSection = sections.find(section => section.id === id) || {};
    const nextFeatures = Array.isArray(features) && features.length > 0 ? [...features] : [];
    if (nextFeatures.length === 0) {
      nextFeatures.push({ component_name: "Cart_Item" });
    }
    nextFeatures[0] = {
      ...nextFeatures[0],
      component_name: nextFeatures[0]?.component_name || "Cart_Item",
      title: label,
      link: href,
      cta: nextFeatures[0]?.cta || undefined,
    };

    updateSection(id, {
      id,
      component_name,
      features: nextFeatures,
      link: href,
      button_color: bg,
      text_color: fg,
      styleSettings: {
        ...(currentSection.styleSettings || {}) // Preserve existing styleSettings
      }
    });
    setIsEditing(false);
  };

  if (isEditing && !previewMode) {
    return (
      <div className="email-component p-3 mb-3 border rounded bg-white">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Centered Button</h4>
          <button className="btn btn-sm btn-primary" onClick={handleSave}>
            Save
          </button>
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Button Text</label>
          <input
            className="form-control"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="View this week’s offers"
          />
        </div>

        <div className="mb-3">
          <label className="form-label fw-bold">Button Link</label>
          <input
            className="form-control"
            value={href}
            onChange={(e) => setHref(e.target.value)}
            placeholder="#"
          />
        </div>

        <div className="row">
          <div className="col-6 mb-3">
            <label className="form-label fw-bold">Background</label>
            <input
              type="color"
              className="form-control form-control-color"
              value={bg}
              onChange={(e) => setBg(e.target.value)}
            />
          </div>
          <div className="col-6 mb-3">
            <label className="form-label fw-bold">Text Color</label>
            <input
              type="color"
              className="form-control form-control-color"
              value={fg}
              onChange={(e) => setFg(e.target.value)}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="email-component mb-3" style={{ backgroundColor }}>
      {!previewMode && (
        <div className="component-controls mb-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <StyleControls 
            id={id} 
            componentName={component_name}
            styleSettings={styleSettings}
            previewMode={previewMode}
          />
          <button className="btn btn-sm btn-outline-primary" onClick={() => setIsEditing(true)}>
            Edit
          </button>
        </div>
      )}

      <div style={containerStyle}>
        <a href={href || "#"} style={buttonStyle}>
          {label}
        </a>
      </div>
    </div>
  );
};

export default DescriptionBtn;


